import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AdminAccount } from "@/api/rest/models/admin";
import {
  addUserToAccount,
  getUserWithDetails,
  listAccounts,
  listUsers,
  removeUserFromAccount,
  sendPasswordResetEmail,
  updateUser,
} from "@/api/rest/services/adminService";
import ConfirmationDialog from "@/components/common/ConfirmationDialog";
import Spinner from "@/components/common/Spinner";

import CollapsibleSection from "./CollapsibleSection";

type SortColumn = "id" | "email" | "name" | "createdAt" | "lastLogin" | "language";
type SortDirection = "asc" | "desc";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "et", label: "Estonian" },
  { code: "lv", label: "Latvian" },
];

const SORT_COLUMNS: SortColumn[] = ["id", "email", "name", "createdAt", "lastLogin", "language"];

const isValidSortColumn = (value: string | null): value is SortColumn => {
  return value !== null && SORT_COLUMNS.includes(value as SortColumn);
};

export default function UsersTab({
  selectedUserId,
  onSelectedUserIdChange,
  onSelectAccount,
  onSelectBuilding,
  onSelectDevelopment,
}: {
  selectedUserId: number | null;
  onSelectedUserIdChange: (userId: number | null) => void;
  onSelectAccount: (accountId: number) => void;
  onSelectBuilding: (buildingId: number) => void;
  onSelectDevelopment: (developmentId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);

  const search = searchParams.get("userSearch") ?? "";
  const setSearch = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("userSearch", value);
    } else {
      newParams.delete("userSearch");
    }
    setSearchParams(newParams, { replace: true });
  };
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const sortColumnParam = searchParams.get("userSort");
  const sortColumn: SortColumn = isValidSortColumn(sortColumnParam) ? sortColumnParam : "id";
  const sortDirection: SortDirection = searchParams.get("userDir") === "desc" ? "desc" : "asc";

  const updateSortParams = (column: SortColumn, direction: SortDirection) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("userSort", column);
    newParams.set("userDir", direction);
    setSearchParams(newParams, { replace: true });
  };

  const usersQuery = useInfiniteQuery({
    queryKey: ["admin-users", search, sortColumn, sortDirection],
    queryFn: ({ pageParam = 0 }) => listUsers(search || undefined, pageParam, 20, sortColumn, sortDirection),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const result = lastPage.result;
      if (!result || result.page >= result.totalPages - 1) {
        return undefined;
      }
      return result.page + 1;
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && usersQuery.hasNextPage && !usersQuery.isFetchingNextPage) {
          usersQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersQuery.hasNextPage, usersQuery.isFetchingNextPage, usersQuery.fetchNextPage]);

  const userDetailsQuery = useQuery({
    queryKey: ["admin-user", selectedUserId],
    queryFn: () => getUserWithDetails(selectedUserId!),
    enabled: selectedUserId !== null,
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, name, language }: { userId: number; name?: string; language?: string }) =>
      updateUser(userId, { name, language }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", selectedUserId] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const sendPasswordResetMutation = useMutation({
    mutationFn: (userId: number) => sendPasswordResetEmail(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", selectedUserId] });
    },
  });

  const addToAccountMutation = useMutation({
    mutationFn: ({ accountId, userId }: { accountId: number; userId: number }) => addUserToAccount(accountId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", selectedUserId] });
      setShowAddAccountModal(false);
    },
  });

  const removeFromAccountMutation = useMutation({
    mutationFn: ({ accountId, userId }: { accountId: number; userId: number }) =>
      removeUserFromAccount(accountId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", selectedUserId] });
    },
  });

  const loadedUsers = usersQuery.data?.pages.flatMap((page) => page.result?.items ?? []) ?? [];
  const userDetails = userDetailsQuery.data?.result;

  // If selected user is not in the loaded list, prepend it from the details query
  const selectedUserInList = selectedUserId ? loadedUsers.some((u) => u.id === selectedUserId) : true;
  const users =
    !selectedUserInList && userDetails
      ? [userDetails.user, ...loadedUsers.filter((u) => u.id !== userDetails.user.id)]
      : loadedUsers;

  // Scroll to selected row when data is loaded and row is available
  useEffect(() => {
    if (selectedUserId && (users.length > 0 || userDetails)) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        if (selectedRowRef.current) {
          selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  }, [selectedUserId, users.length, userDetails]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      updateSortParams(column, sortDirection === "asc" ? "desc" : "asc");
    } else {
      updateSortParams(column, "asc");
    }
  };

  const SortIndicator = ({ column }: { column: SortColumn }) => (
    <span className="ml-1">{sortColumn === column ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
  );

  const renderExpandedRow = (userId: number) => {
    if (selectedUserId !== userId) {
      return null;
    }

    return (
      <tr key={`details-${userId}`}>
        <td colSpan={7} className="p-0 border-b">
          <div className="bg-gray-50 p-4 border-t">
            {userDetailsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : userDetails ? (
              <UserDetailsPanel
                userDetails={userDetails}
                onUpdateName={(name) => updateUserMutation.mutate({ userId: selectedUserId, name })}
                onUpdateLanguage={(language) => updateUserMutation.mutate({ userId: selectedUserId, language })}
                onSendPasswordReset={() => sendPasswordResetMutation.mutate(selectedUserId)}
                onAddToAccount={() => setShowAddAccountModal(true)}
                onRemoveFromAccount={(accountId) =>
                  removeFromAccountMutation.mutate({ accountId, userId: selectedUserId })
                }
                onSelectAccount={onSelectAccount}
                onSelectBuilding={onSelectBuilding}
                onSelectDevelopment={onSelectDevelopment}
                onClose={() => onSelectedUserIdChange(null)}
                isUpdating={updateUserMutation.isPending}
                isSendingPasswordReset={sendPasswordResetMutation.isPending}
                isRemovingFromAccount={removeFromAccountMutation.isPending}
              />
            ) : (
              <div className="text-red-500">Failed to load user details</div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search users by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dobu-orange"
        />
      </div>

      {usersQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      ) : usersQuery.isError ? (
        <div className="text-red-500 py-4">Failed to load users</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("id")}
                  >
                    ID
                    <SortIndicator column="id" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("email")}
                  >
                    Email
                    <SortIndicator column="email" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("name")}
                  >
                    Name
                    <SortIndicator column="name" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("createdAt")}
                  >
                    Created At
                    <SortIndicator column="createdAt" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("lastLogin")}
                  >
                    Last Login
                    <SortIndicator column="lastLogin" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("language")}
                  >
                    Language
                    <SortIndicator column="language" />
                  </th>
                  <th className="p-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr
                      ref={selectedUserId === user.id ? selectedRowRef : null}
                      className={`border-b cursor-pointer hover:bg-gray-50 ${
                        selectedUserId === user.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() => onSelectedUserIdChange(selectedUserId === user.id ? null : user.id)}
                    >
                      <td className="p-3">{user.id}</td>
                      <td className="p-3">{user.email}</td>
                      <td className="p-3">{user.name}</td>
                      <td className="p-3">{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="p-3">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "-"}</td>
                      <td className="p-3">{user.language ?? "-"}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            sendPasswordResetMutation.mutate(user.id);
                          }}
                          disabled={
                            sendPasswordResetMutation.isPending && sendPasswordResetMutation.variables === user.id
                          }
                          className="btn-custom px-2 py-1 text-xs bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50 transition-colors"
                          title="Send password reset email"
                        >
                          {sendPasswordResetMutation.isPending && sendPasswordResetMutation.variables === user.id
                            ? "Sending..."
                            : "Reset Password"}
                        </button>
                      </td>
                    </tr>
                    {renderExpandedRow(user.id)}
                  </React.Fragment>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div ref={loadMoreRef} className="flex justify-center py-4">
            {usersQuery.isFetchingNextPage && <Spinner className="w-6 h-6" />}
          </div>
        </>
      )}

      {showAddAccountModal && selectedUserId && userDetails && (
        <AddToAccountModal
          existingAccountIds={userDetails.accounts.map((a) => a.id)}
          onClose={() => setShowAddAccountModal(false)}
          onAdd={(accountId) => addToAccountMutation.mutate({ accountId, userId: selectedUserId })}
          isLoading={addToAccountMutation.isPending}
        />
      )}
    </div>
  );
}

function UserDetailsPanel({
  userDetails,
  onUpdateName,
  onUpdateLanguage,
  onSendPasswordReset,
  onAddToAccount,
  onRemoveFromAccount,
  onSelectAccount,
  onSelectBuilding,
  onSelectDevelopment,
  onClose,
  isUpdating,
  isSendingPasswordReset,
  isRemovingFromAccount,
}: {
  userDetails: NonNullable<Awaited<ReturnType<typeof getUserWithDetails>>["result"]>;
  onUpdateName: (name: string) => void;
  onUpdateLanguage: (language: string) => void;
  onSendPasswordReset: () => void;
  onAddToAccount: () => void;
  onRemoveFromAccount: (accountId: number) => void;
  onSelectAccount: (accountId: number) => void;
  onSelectBuilding: (buildingId: number) => void;
  onSelectDevelopment: (developmentId: number) => void;
  onClose: () => void;
  isUpdating: boolean;
  isSendingPasswordReset: boolean;
  isRemovingFromAccount: boolean;
}) {
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(userDetails.user.name);
  const [pendingRemoveFromAccount, setPendingRemoveFromAccount] = useState<{
    accountId: number;
    name: string;
  } | null>(null);

  const handleSaveName = () => {
    if (nameValue.trim() && nameValue !== userDetails.user.name) {
      onUpdateName(nameValue.trim());
    }
    setEditingName(false);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-3 border-b border-gray-200">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{userDetails.user.email}</h2>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            {editingName ? (
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="px-2 py-0.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-dobu-orange"
                  autoFocus
                />
                <button
                  onClick={handleSaveName}
                  disabled={isUpdating}
                  className="btn-custom px-2 py-0.5 bg-dobu-orange text-white text-xs rounded hover:bg-dobu-orange/90 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingName(false);
                    setNameValue(userDetails.user.name);
                  }}
                  className="btn-custom px-2 py-0.5 border text-xs rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <span
                className="cursor-pointer hover:text-dobu-orange transition-colors"
                onClick={() => setEditingName(true)}
                title="Click to edit name"
              >
                {userDetails.user.name}
              </span>
            )}
            <span>•</span>
            <span>ID: {userDetails.user.id}</span>
            <span>•</span>
            <select
              value={userDetails.user.language ?? ""}
              onChange={(e) => onUpdateLanguage(e.target.value || "en")}
              disabled={isUpdating}
              className="px-1 py-0.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-dobu-orange disabled:opacity-50 bg-white"
            >
              <option value="">Language not set</option>
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={onClose}
          className="btn-custom p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
          title="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Collapsible Sections */}
      <div className="px-4">
        {/* Accounts Section */}
        <CollapsibleSection
          title="Accounts"
          count={userDetails.accounts.length}
          action={
            <button
              onClick={onAddToAccount}
              className="btn-custom px-2 py-1 text-xs font-medium text-dobu-orange hover:bg-orange-50 rounded transition-colors"
            >
              + Add
            </button>
          }
        >
          {userDetails.accounts.length === 0 ? (
            <p className="text-gray-500 text-sm">No accounts</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {userDetails.accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div
                    onClick={() => onSelectAccount(account.id)}
                    className="cursor-pointer hover:text-dobu-orange transition-colors min-w-0 flex-1"
                  >
                    <div className="font-medium text-sm truncate">{account.name}</div>
                    <div className="text-xs text-gray-500">{account.type}</div>
                  </div>
                  <button
                    onClick={() => setPendingRemoveFromAccount({ accountId: account.id, name: account.name })}
                    disabled={isRemovingFromAccount}
                    className="btn-custom ml-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Remove from account"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Households Section */}
        <CollapsibleSection
          title="Households"
          count={userDetails.developments.length > 0 ? userDetails.developments.length : undefined}
          defaultOpen={userDetails.developments.length > 0}
        >
          {userDetails.developments.length === 0 ? (
            <p className="text-gray-500 text-sm">No households</p>
          ) : (
            <div className="space-y-3">
              {userDetails.developments.map((dev, idx) => (
                <div
                  key={dev.development?.id ?? `standalone-${idx}`}
                  className="p-3 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    {dev.development ? (
                      <span
                        className="font-medium text-sm text-gray-900 cursor-pointer hover:text-dobu-orange transition-colors"
                        onClick={() => onSelectDevelopment(dev.development!.id)}
                      >
                        {dev.development.name}
                      </span>
                    ) : (
                      <span className="font-medium text-sm text-gray-900">Standalone Buildings</span>
                    )}
                    {dev.isDevelopmentAdmin && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                        Dev. Admin
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {dev.buildings.map((building) => (
                      <div key={building.building.id} className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <div className="flex-1 min-w-0">
                          <span
                            className="cursor-pointer hover:text-dobu-orange transition-colors"
                            onClick={() => onSelectBuilding(building.building.id)}
                          >
                            {building.building.street}, {building.building.city}
                          </span>
                          {building.isDirectAdmin && !dev.isDevelopmentAdmin && (
                            <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                              Admin
                            </span>
                          )}
                          <span className="text-gray-400 ml-2">
                            {building.households.length > 0
                              ? building.households.map((h, idx) => (
                                  <span key={h.id}>
                                    {idx > 0 && ", "}
                                    <span
                                      className="cursor-pointer hover:text-dobu-orange transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/household/${h.id}`);
                                      }}
                                    >
                                      {h.title}
                                    </span>
                                  </span>
                                ))
                              : "No households"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {dev.buildings.length === 0 && <span className="text-gray-400 italic">No buildings</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Mobile Devices Section */}
        <CollapsibleSection
          title="Mobile Devices"
          count={userDetails.deviceTokens.length}
          defaultOpen={userDetails.deviceTokens.length > 0}
        >
          {userDetails.deviceTokens.length === 0 ? (
            <p className="text-gray-500 text-sm">No mobile devices</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {userDetails.deviceTokens.map((device) => (
                <div key={device.id} className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{device.deviceType ?? "Unknown device"}</span>
                    <span
                      className={`px-2 py-0.5 text-xs rounded font-medium ${device.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                    >
                      {device.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {device.appVersion && <span className="mr-2">v{device.appVersion}</span>}
                    <span>Registered: {new Date(device.createdAt).toLocaleDateString()}</span>
                    <span className="ml-2">
                      Last used: {device.lastUsedAt ? new Date(device.lastUsedAt).toLocaleDateString() : "Never"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Notification Settings Section */}
        <CollapsibleSection
          title="Notification Settings"
          count={userDetails.notificationSettings.length}
          defaultOpen={false}
        >
          {userDetails.notificationSettings.length === 0 ? (
            <p className="text-gray-500 text-sm">No notification settings</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {userDetails.notificationSettings.map((setting) => (
                <div key={setting.notificationType} className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="font-medium text-sm">{formatNotificationType(setting.notificationType)}</div>
                  <div className="flex gap-4 text-xs text-gray-500 mt-1">
                    <span>
                      Email:{" "}
                      <span className={setting.emailEnabled ? "text-green-600" : "text-gray-400"}>
                        {setting.emailEnabled ? "On" : "Off"}
                      </span>
                    </span>
                    <span>
                      Mobile:{" "}
                      <span className={setting.mobileEnabled ? "text-green-600" : "text-gray-400"}>
                        {setting.mobileEnabled ? "On" : "Off"}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Actions Section */}
        <CollapsibleSection title="Actions" defaultOpen={false}>
          <div className="space-y-3">
            {/* Password Reset */}
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Password Reset</p>
                  <p className="text-xs text-gray-500">
                    Last sent:{" "}
                    {userDetails.lastPasswordResetSentAt
                      ? new Date(userDetails.lastPasswordResetSentAt).toLocaleString()
                      : "Never"}
                  </p>
                </div>
                <button
                  onClick={onSendPasswordReset}
                  disabled={isSendingPasswordReset}
                  className="btn-custom px-3 py-1.5 bg-dobu-orange text-white text-sm rounded hover:bg-dobu-orange/90 disabled:opacity-50 transition-colors"
                >
                  {isSendingPasswordReset ? "Sending..." : "Send Reset Email"}
                </button>
              </div>
            </div>

            {/* User Info */}
            <div className="p-3 bg-gray-100 rounded-lg text-xs text-gray-500">
              <div className="flex gap-4">
                <span>Created: {new Date(userDetails.user.createdAt).toLocaleString()}</span>
                <span>
                  Last Login:{" "}
                  {userDetails.user.lastLogin ? new Date(userDetails.user.lastLogin).toLocaleString() : "Never"}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <ConfirmationDialog
        isOpen={pendingRemoveFromAccount !== null}
        onCancel={() => setPendingRemoveFromAccount(null)}
        onConfirm={() => {
          if (pendingRemoveFromAccount) {
            onRemoveFromAccount(pendingRemoveFromAccount.accountId);
            setPendingRemoveFromAccount(null);
          }
        }}
        title="Remove from Account"
        message={`Are you sure you want to remove this user from "${pendingRemoveFromAccount?.name}"?`}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
}

function formatNotificationType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function AddToAccountModal({
  existingAccountIds,
  onClose,
  onAdd,
  isLoading,
}: {
  existingAccountIds: number[];
  onClose: () => void;
  onAdd: (accountId: number) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<AdminAccount | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["admin-accounts-for-add", search],
    queryFn: () => listAccounts(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableAccounts = (accountsQuery.data?.result?.items ?? []).filter((a) => !existingAccountIds.includes(a.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Add User to Account</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Search Account</label>
            <input
              type="text"
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedAccount(null);
              }}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>

          {accountsQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableAccounts.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableAccounts.map((account) => (
                <li
                  key={account.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedAccount?.id === account.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedAccount(account)}
                >
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-gray-500">{account.type}</div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !accountsQuery.isLoading && availableAccounts.length === 0 && (
            <div className="text-gray-500 text-center py-2">No accounts found</div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-custom px-4 py-2 border rounded hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={() => selectedAccount && onAdd(selectedAccount.id)}
              disabled={isLoading || !selectedAccount}
              className="btn-custom px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
            >
              {isLoading ? "Adding..." : "Add to Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
